import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import {
  ensureExportCaptureWrapper,
  inlineDocumentStylesIntoClone,
  PDF_CAPTURE_WIDTH,
  prepareTeamHistoryPdfCaptureClone,
} from "./pdfDomBuilders";
import { loadPdfLogoDataUrl } from "./pdfLogoLoader";

const PDF_PAGE_WIDTH = 1200;
const PDF_PAGE_HEIGHT = 1400;
const PDF_MARGIN = { top: 60, right: 40, bottom: 40, left: 40 };
const MAX_CANVAS_DIMENSION = 16384;
// Sub-point leftovers from float division should not spill onto a new page.
const MIN_REMAINING_HEIGHT = 1;

// Crops trailing blank (near-white) rows off the bottom of a captured canvas.
// The Team Comparison export captures the whole page as one tall canvas, so any
// empty space beneath the last visible content would otherwise be sliced onto a
// blank trailing page.
function trimCanvasBottomWhitespace(canvas) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return canvas;

  const { width, height } = canvas;
  if (!width || !height) return canvas;

  const WHITE_THRESHOLD = 250;
  const columnStep = Math.max(1, Math.floor(width / 64));

  let lastContentRow = -1;

  try {
    for (let y = height - 1; y >= 0; y -= 1) {
      const row = ctx.getImageData(0, y, width, 1).data;
      let hasContent = false;

      for (let x = 0; x < width; x += columnStep) {
        const idx = x * 4;
        const r = row[idx];
        const g = row[idx + 1];
        const b = row[idx + 2];
        const a = row[idx + 3];

        if (
          a !== 0 &&
          (r < WHITE_THRESHOLD ||
            g < WHITE_THRESHOLD ||
            b < WHITE_THRESHOLD)
        ) {
          hasContent = true;
          break;
        }
      }

      if (hasContent) {
        lastContentRow = y;
        break;
      }
    }
  } catch (error) {
    // getImageData can throw on tainted canvases; fall back to the full canvas.
    console.warn("PDF export: unable to trim canvas whitespace", error);
    return canvas;
  }

  if (lastContentRow < 0) return canvas;

  const trimmedHeight = lastContentRow + 1;
  if (trimmedHeight >= height - 1) return canvas;

  const trimmedCanvas = document.createElement("canvas");
  trimmedCanvas.width = width;
  trimmedCanvas.height = trimmedHeight;

  const trimmedCtx = trimmedCanvas.getContext("2d");
  trimmedCtx.drawImage(
    canvas,
    0,
    0,
    width,
    trimmedHeight,
    0,
    0,
    width,
    trimmedHeight
  );

  return trimmedCanvas;
}

function getSafeCaptureScale(width, height) {
  const baseScale = Math.min(2, window.devicePixelRatio || 1);

  if (!width || !height) {
    return baseScale;
  }

  const dimensionScale = Math.min(
    MAX_CANVAS_DIMENSION / width,
    MAX_CANVAS_DIMENSION / height
  );

  return Math.max(0.75, Math.min(baseScale, dimensionScale));
}

export async function captureElementToCanvas(element, extraOptions = {}) {
  if (!element) {
    throw new Error("Capture element not found");
  }

  const rect = element.getBoundingClientRect();
  const scrollHeight =
    element.scrollHeight || element.clientHeight || rect.height;
  const scrollWidth = PDF_CAPTURE_WIDTH;

  if (!scrollHeight) {
    throw new Error("Capture element has zero dimensions");
  }

  const scale = getSafeCaptureScale(
    scrollWidth * Math.min(2, window.devicePixelRatio || 1),
    scrollHeight * Math.min(2, window.devicePixelRatio || 1)
  );

  const rawCanvas = await html2canvas(element, {
    scale,
    useCORS: true,
    backgroundColor: "#ffffff",
    logging: false,
    imageTimeout: 15000,
    windowWidth: PDF_CAPTURE_WIDTH,
    width: PDF_CAPTURE_WIDTH,
    windowHeight: scrollHeight,
    height: scrollHeight,
    onclone: (clonedDoc, clonedElement) => {
      // Inline the real application CSS into the clone first so the capture is
      // styled like the on-screen UI even in production builds (external
      // stylesheet links don't re-resolve inside the html2canvas iframe).
      inlineDocumentStylesIntoClone(clonedDoc);
      const captureRoot = ensureExportCaptureWrapper(clonedDoc, clonedElement);
      prepareTeamHistoryPdfCaptureClone(captureRoot);
    },
    ...extraOptions,
  });

  if (!rawCanvas.width || !rawCanvas.height) {
    throw new Error("html2canvas returned an empty canvas");
  }

  const canvas = trimCanvasBottomWhitespace(rawCanvas);

  let dataUrl = "";
  try {
    dataUrl = canvas.toDataURL("image/jpeg", 0.95);
  } catch (error) {
    throw new Error(`Canvas export failed: ${error?.message || error}`);
  }

  if (!dataUrl || dataUrl === "data:,") {
    throw new Error("Canvas toDataURL returned empty output");
  }

  return { canvas, scale, scrollWidth, scrollHeight };
}

function addLogoToPage(pdf, logoDataUrl) {
  pdf.addImage(logoDataUrl, "PNG", PDF_MARGIN.left, 20, 92, 33);
}

function addCanvasToPdf(pdf, canvas, state) {
  const contentWidth = PDF_PAGE_WIDTH - PDF_MARGIN.left - PDF_MARGIN.right;
  const fullImageHeight = (canvas.height * contentWidth) / canvas.width;

  let remainingImageHeight = fullImageHeight;
  let sourceY = 0;

  while (remainingImageHeight > MIN_REMAINING_HEIGHT) {
    const availableHeight = PDF_PAGE_HEIGHT - state.y - PDF_MARGIN.bottom;

    if (availableHeight <= 0) {
      pdf.addPage([PDF_PAGE_WIDTH, PDF_PAGE_HEIGHT]);
      state.pageNumber += 1;
      state.y = PDF_MARGIN.top;
      continue;
    }

    const sliceHeight = Math.min(remainingImageHeight, availableHeight);
    const sliceHeightPx = (sliceHeight / fullImageHeight) * canvas.height;

    const sliceCanvas = document.createElement("canvas");
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = Math.max(1, Math.ceil(sliceHeightPx));

    const ctx = sliceCanvas.getContext("2d");
    ctx.drawImage(
      canvas,
      0,
      sourceY,
      canvas.width,
      sliceHeightPx,
      0,
      0,
      canvas.width,
      sliceHeightPx
    );

    const sliceData = sliceCanvas.toDataURL("image/jpeg", 0.95);
    pdf.addImage(
      sliceData,
      "JPEG",
      PDF_MARGIN.left,
      state.y,
      contentWidth,
      sliceHeight
    );

    state.y += sliceHeight + 12;
    sourceY += sliceHeightPx;
    remainingImageHeight -= sliceHeight;

    if (remainingImageHeight > MIN_REMAINING_HEIGHT) {
      pdf.addPage([PDF_PAGE_WIDTH, PDF_PAGE_HEIGHT]);
      state.pageNumber += 1;
      state.y = PDF_MARGIN.top;
    }
  }
}

export async function generatePDFFromSections({
  sections,
  fileName,
  svgLogoPath,
  dateTimeString,
}) {
  if (!sections?.length) {
    throw new Error("No PDF sections provided");
  }

  const logoDataUrl = await loadPdfLogoDataUrl(svgLogoPath);
  const pdf = new jsPDF({
    unit: "pt",
    format: [PDF_PAGE_WIDTH, PDF_PAGE_HEIGHT],
    orientation: "portrait",
  });

  const state = {
    y: PDF_MARGIN.top,
    pageNumber: 1,
  };

  if (dateTimeString) {
    pdf.setFontSize(18);
    pdf.setTextColor(100);
    const textWidth = pdf.getTextWidth(dateTimeString);
    pdf.text(
      dateTimeString,
      PDF_PAGE_WIDTH - PDF_MARGIN.right - textWidth,
      PDF_MARGIN.top - 18
    );
    state.y = PDF_MARGIN.top;
  }

  for (let index = 0; index < sections.length; index += 1) {
    const section = sections[index];
    const sectionName =
      section.getAttribute?.("data-team-history-export-section") ||
      `section-${index}`;

    try {
      const { canvas } = await captureElementToCanvas(section);
      addCanvasToPdf(pdf, canvas, state);
    } catch (error) {
      console.error(`Failed to capture PDF section "${sectionName}":`, error);
      throw error;
    }
  }

  const totalPages = pdf.internal.getNumberOfPages();
  for (let page = 1; page <= totalPages; page += 1) {
    pdf.setPage(page);
    if (page > 1 && logoDataUrl) {
      addLogoToPage(pdf, logoDataUrl);
    }

    pdf.setFontSize(12);
    pdf.setTextColor(80);
    const pageNumberText = `Page ${page} of ${totalPages}`;
    pdf.text(
      pageNumberText,
      PDF_PAGE_WIDTH - PDF_MARGIN.right - 60,
      PDF_PAGE_HEIGHT - 20
    );
  }

  pdf.save(fileName);
}
